import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resolvePrefixToPane, LEGACY_PREFIX_MAP } from '../route-registry';

export type PaneType = 'product' | 'success' | 'sales' | 'ops' | 'archie' | 'documentation';

export interface PaneInfo {
  id: PaneType;
  name: string;
  description: string;
  basePath: string;
  defaultPath: string;
  urlPrefix: string;
}

// NB: Object insertion order is the sidebar render order (Sidebar.tsx uses
// `Object.keys(PANES).map(...)`). Keep `documentation` first: it's the primary
// Content workspace and should sit at the top of the pane switcher.
export const PANES: Record<PaneType, PaneInfo> = {
  documentation: {
    id: 'documentation',
    name: 'Content',
    description: 'Document Workspace',
    basePath: '/docs',
    defaultPath: '/docs',
    urlPrefix: 'docs',
  },
  product: {
    id: 'product',
    name: 'Product',
    description: 'Development Platform',
    basePath: '/product',
    defaultPath: '/product',
    urlPrefix: 'product',
  },
  success: {
    id: 'success',
    name: 'Success & Support',
    description: 'Customer Success',
    basePath: '/success',
    defaultPath: '/success',
    urlPrefix: 'success',
  },
  sales: {
    id: 'sales',
    name: 'Sales & Marketing',
    description: 'Pipeline, Campaigns & Content',
    basePath: '/sales',
    defaultPath: '/sales',
    urlPrefix: 'sales',
  },
  ops: {
    id: 'ops',
    name: 'Operations',
    description: 'Finance, People & Admin',
    basePath: '/operations',
    defaultPath: '/operations',
    urlPrefix: 'operations',
  },
  archie: {
    id: 'archie',
    name: 'Archie',
    description: 'AI & Infrastructure',
    basePath: '/archie',
    defaultPath: '/archie',
    urlPrefix: 'archie',
  },
};

interface PaneContextValue {
  activePane: PaneType;
  paneInfo: PaneInfo;
  setActivePane: (pane: PaneType) => void;
  switchPane: (pane: PaneType) => void;
}

const PaneContext = createContext<PaneContextValue | null>(null);

const PANE_STORAGE_KEY = 'martha.activePane';

/**
 * Determines the active pane from the current URL path.
 * Returns null for neutral routes (topics, settings, tasks) so the caller
 * can preserve the current pane instead of resetting to 'product'.
 */
function getPaneFromPath(pathname: string): PaneType | null {
  // New workspace-scoped: /w/:wsSlug/:prefix/...
  const wsMatch = pathname.match(/^\/w\/[^/]+\/([^/]+)/);
  const prefix = wsMatch?.[1];
  if (prefix) {
    // Try new semantic prefix first
    const resolved = resolvePrefixToPane(prefix);
    if (resolved) return resolved;

    // Try legacy 2-letter prefix (for redirects in flight)
    const mappedPrefix = LEGACY_PREFIX_MAP[prefix];
    if (mappedPrefix) return resolvePrefixToPane(mappedPrefix);
  }

  // Legacy flat routes (keep for transition)
  if (pathname.startsWith('/archie') || pathname.startsWith('/ar')) return 'archie';
  if (pathname.startsWith('/sales') || pathname.startsWith('/sm')) return 'sales';
  if (pathname.startsWith('/operations') || pathname.startsWith('/op')) return 'ops';
  if (pathname.startsWith('/success') || pathname.startsWith('/su')) return 'success';
  if (pathname.startsWith('/product') || pathname.startsWith('/pr') || pathname === '/') return 'product';
  if (pathname.startsWith('/docs') || pathname.startsWith('/dc')) return 'documentation';
  return null; // Unknown route — preserve current pane
}

/**
 * Read last-used pane from localStorage (for neutral routes only).
 */
function getStoredPane(): PaneType {
  try {
    const stored = localStorage.getItem(PANE_STORAGE_KEY) as PaneType | null;
    if (stored && stored in PANES) return stored;
  } catch { /* noop */ }
  return 'product';
}

interface PaneProviderProps {
  children: ReactNode;
}

export const PaneProvider = ({ children }: PaneProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Pure derivation: activePane comes from URL. For neutral routes, fall back to localStorage.
  const activePane = useMemo<PaneType>(() => {
    const fromUrl = getPaneFromPath(location.pathname);
    if (fromUrl) return fromUrl;
    // Neutral route (settings, tasks) — use stored pane for sidebar display
    return getStoredPane();
  }, [location.pathname]);

  const paneInfo = PANES[activePane];

  // setActivePane is a no-op for external callers — pane is derived from URL.
  // Kept for API compatibility; callers should use switchPane instead.
  const setActivePane = useCallback((_pane: PaneType) => {
    // No-op: pane is derived from URL
  }, []);

  const switchPane = useCallback((pane: PaneType) => {
    // Persist to localStorage for neutral-route fallback
    try { localStorage.setItem(PANE_STORAGE_KEY, pane); } catch { /* noop */ }
    // Navigate to the new pane
    const wsMatch = location.pathname.match(/^\/w\/([^/]+)/);
    if (wsMatch) {
      navigate(`/w/${wsMatch[1]}/${PANES[pane].urlPrefix}`);
    } else {
      navigate(PANES[pane].defaultPath);
    }
  }, [navigate, location.pathname]);

  const contextValue = useMemo(() => ({
    activePane,
    paneInfo,
    setActivePane,
    switchPane,
  }), [activePane, paneInfo, setActivePane, switchPane]);

  return (
    <PaneContext.Provider value={contextValue}>
      {children}
    </PaneContext.Provider>
  );
};

export const usePane = (): PaneContextValue => {
  const context = useContext(PaneContext);
  if (!context) {
    throw new Error('usePane must be used within a PaneProvider');
  }
  return context;
};
